// What the announcement form holds, and the two rules about it that come from
// the descriptor rather than from the screen: where a new form starts, and
// which repeats a window allows. The screen itself is the legacy drawer, in
// features/drawer.tsx.

import { defaultWindow, modeOf } from "~/components/clock/clock";
import { format } from "~/components/clock/dates";
import type { FormDescriptor, RepeatOption } from "~/api";

export type FormValue = {
  companyMessageId: number;
  startsAt: string | null;
  endsAt: string | null;
  proxyUserId: number;
  displayName: string;
  notify: string;
  repeat: string;
};

/**
 * initialValue is the descriptor's own defaults, anchored to now.
 *
 * The window starts in the quarter hour that is running and, where the
 * announcement has an end, ends a day later — or a quarter of an hour later
 * for a time announcement. That is where the legacy clock started too.
 */
export function initialValue(d: FormDescriptor): FormValue {
  const w = defaultWindow(modeOf(d));
  return {
    companyMessageId: d.companyMessageId,
    startsAt: d.start ? format(w.start) : null,
    endsAt: d.end && w.end ? format(w.end) : null,
    proxyUserId: d.proxy?.options.find((o) => o.selected)?.id ?? 0,
    displayName: "",
    notify: d.notify?.default ?? "none",
    repeat: "none",
  };
}

/**
 * allowedRepeats applies the limits the server sent as data.
 *
 * The rule is that a window may not be longer than the interval it repeats on,
 * or the series would overlap itself: a day-long absence can repeat daily,
 * weekly, monthly and yearly, a five-day one weekly and up, a three-week one
 * only monthly and yearly. An absence with no end has nothing to repeat.
 *
 * The limits themselves come from the server. The legacy interface had them as
 * constants inside the drawer component, so changing one meant redeploying.
 */
export function allowedRepeats(options: RepeatOption[], start: string | null, end: string | null): RepeatOption[] {
  if (!start || !end) return options.filter((o) => o.value === "none");
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (!Number.isFinite(ms) || ms < 0) return options.filter((o) => o.value === "none");
  const hours = ms / 3_600_000;
  const days = hours / 24;
  return options.filter((o) => {
    // Not longer than, rather than shorter than: a tagesgenau absence from the
    // first to the second is exactly twenty-four hours and repeats daily.
    if (o.maxDurationHours !== undefined) return hours <= o.maxDurationHours;
    if (o.maxDurationDays !== undefined) return days <= o.maxDurationDays;
    return true;
  });
}
